import { Button } from "@/components/flexkit/Button";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { DeleteRunnerModal, ForceDeleteRunnerModal } from "@/components/runners/DeleteRunnerModal";
import { RenameRunnerModal } from "@/components/runners/RenameRunnerModal";
import { useCurrentMember } from "@/hooks/use-current-member";
import type { PlainRunner } from "@/queries/runner-queries";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { MoreHorizontalIcon } from "lucide-react";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const RunnerActionsDropdown: FC<{
    runner: PlainRunner;
    button?: React.ReactNode;
    showViewDetails: boolean;
    onDeleteSuccessful?: () => void;
}> = ({ runner, button, showViewDetails, onDeleteSuccessful }) => {
    const navigate = useNavigate();

    const { currentMember } = useCurrentMember();

    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const { toast } = useToast();

    const version = runner.status?.version || "unknown";

    const handleViewDetails = useCallback(() => {
        navigate(`/settings/runners/${runner.runnerId}`);
    }, [navigate, runner.runnerId]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(runner.runnerId);
        toast({
            title: `Runner ID copied to clipboard`,
            description: runner.runnerId,
        });
    }, [toast, runner.runnerId]);

    const handleCopyVersion = useCallback(async () => {
        await navigator.clipboard.writeText(version);
        toast({
            title: `Runner version copied to clipboard`,
            description: version,
        });
    }, [toast, version]);

    const isCreator =
        runner.creator?.principal === Principal.USER
            ? runner.creator?.id === (currentMember?.userId || "no-member")
            : false;
    const isAdmin = currentMember?.role === OrganizationRole.ADMIN;
    const canEdit = isAdmin || isCreator;
    const canViewDetails = runner.kind === RunnerKind.REMOTE && canEdit;
    const showForceDelete = runner.spec?.desiredPhase === RunnerPhase.DELETED;

    const handleViewEnvironments = useCallback(() => {
        navigate(`/settings/environments?runner=${runner.runnerId}`);
    }, [navigate, runner.runnerId]);

    return (
        // Ensure that clicks in the actions menu or modals don't propagate to parent elements
        <div onClick={(e) => e.stopPropagation()}>
            <DropdownActions
                button={
                    button || (
                        <Button
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            data-testid="runner-actions-dropdown"
                            aria-label="More actions"
                        >
                            <MoreHorizontalIcon className="h-5 w-5 text-content-primary" />
                        </Button>
                    )
                }
                triggerTestId="runner-actions-dropdown-trigger"
            >
                {canViewDetails && showViewDetails && (
                    <DropdownMenuItem onClick={handleViewDetails}>View Details</DropdownMenuItem>
                )}
                {isAdmin && <DropdownMenuItem onClick={handleViewEnvironments}>View Environments</DropdownMenuItem>}
                <DropdownMenuItem onClick={handleCopyId}>Copy ID</DropdownMenuItem>
                <DropdownMenuItem onClick={handleCopyVersion}>Copy Version</DropdownMenuItem>
                {canEdit && (
                    <>
                        <DropdownMenuSeparator className="bg-content-tertiary/20" />
                        <DropdownMenuItem onClick={() => setShowRenameModal(true)}>Rename</DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-red-500 focus:text-red-500"
                            onClick={() => {
                                if (showForceDelete) {
                                    setShowForceDeleteModal(true);
                                } else {
                                    setShowDeletionModal(true);
                                }
                            }}
                        >
                            {showForceDelete ? "Force Delete" : "Delete"}
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownActions>

            <DeleteRunnerModal
                open={showDeletionModal}
                runner={runner}
                onClose={() => setShowDeletionModal(false)}
                onDeleteSuccessful={onDeleteSuccessful}
            />
            <ForceDeleteRunnerModal
                open={showForceDeleteModal}
                runner={runner}
                onClose={() => setShowForceDeleteModal(false)}
                onDeleteSuccessful={onDeleteSuccessful}
            />
            <RenameRunnerModal open={showRenameModal} runner={runner} onClose={() => setShowRenameModal(false)} />
        </div>
    );
};
